import { action, computed, makeObservable, observable } from 'mobx'
import { MapRef } from 'react-map-gl/mapbox'

import { GlobeViewMapType } from '~/client/graph'
import { IMapSettings } from '~/client/src/shared/components/MapBoxViewer/mapSettings'
import {
  basemapStyleFor,
  nextProjection,
  Projection,
} from '~/client/src/shared/map/mapBasemapRule'

import { isSatelliteStyle } from '../MapBoxViewer/mapSettings'

export { Projection }

// Deliberately a SUBSET of the generated IGlobeViewStyle: this surface has no zoom-scaling,
// overlap or pin-label toggles, so its mapSettings is genuinely smaller rather than a copy that
// drifted. It shares isSatelliteActive with the other hosts and nothing else.
interface IGlobeViewStyle {
  mapType: GlobeViewMapType
  isThreeDEnabled: boolean
  isTrafficEnabled: boolean
  isTerrainEnabled: boolean
  shouldHideLabels: boolean
}

interface IInitialViewport {
  latitude: number
  longitude: number
  zoom: number
}

export default class MapControlsStore {
  @observable public projection: Projection = Projection.mercator
  @observable public globeViewStyle: IGlobeViewStyle = {
    mapType: GlobeViewMapType.Street,
    isThreeDEnabled: false,
    isTrafficEnabled: false,
    isTerrainEnabled: false,
    shouldHideLabels: false,
  }

  private mapRef: MapRef | null = null
  private initialViewport: IInitialViewport
  private mapReadyCallbacks: Array<(map: mapboxgl.Map) => void> = []

  public constructor(initialViewport: IInitialViewport) {
    this.initialViewport = initialViewport
    makeObservable(this)
  }

  public updateInitialViewport(viewport: IInitialViewport): void {
    this.initialViewport = viewport
  }

  public setMapRef(ref: MapRef | null): void {
    this.mapRef = ref

    if (ref) {
      const map = ref.getMap()
      // Kept, not drained: a recovery hands over a new instance and every consumer has
      // to be told again (STX1-1068).
      this.mapReadyCallbacks.forEach(cb => cb(map))
    }
  }

  public onMapReady(callback: (map: mapboxgl.Map) => void): () => void {
    this.mapReadyCallbacks.push(callback)
    const map = this.getMap()
    if (map) {
      callback(map)
    }
    return () => {
      this.mapReadyCallbacks = this.mapReadyCallbacks.filter(
        cb => cb !== callback,
      )
    }
  }

  @computed
  public get isSatelliteActive(): boolean {
    return isSatelliteStyle(this.globeViewStyle)
  }

  @computed
  public get mapSettings(): IMapSettings {
    return {
      mapType: this.isSatelliteActive ? 'satellite' : 'street',
      isThreeDEnabled: this.globeViewStyle.isThreeDEnabled,
      isTrafficEnabled: this.globeViewStyle.isTrafficEnabled,
      isTerrainEnabled: this.globeViewStyle.isTerrainEnabled,
      shouldHideLabels: this.globeViewStyle.shouldHideLabels,
      onMapTypeChange: this.toggleMapStyle,
      onToggle3D: this.toggle3DMode,
      onToggleTraffic: this.toggleTraffic,
      onToggleTerrain: this.toggleTerrain,
      onToggleLabels: this.toggleLabels,
    }
  }

  @computed
  public get mapStyle(): string {
    return basemapStyleFor(this.globeViewStyle.mapType)
  }

  @action.bound
  public toggleProjection(): void {
    this.projection = nextProjection(this.projection)
  }

  @action.bound
  public toggleMapStyle(): void {
    this.globeViewStyle.mapType =
      this.globeViewStyle.mapType === GlobeViewMapType.Street
        ? GlobeViewMapType.Satellite
        : GlobeViewMapType.Street

    this.globeViewStyle.isTrafficEnabled = false
    this.globeViewStyle.isThreeDEnabled = false
    this.globeViewStyle.isTerrainEnabled = false
  }

  @action.bound
  public toggleTraffic(): void {
    this.globeViewStyle.isTrafficEnabled = !this.globeViewStyle.isTrafficEnabled
  }

  @action.bound
  public toggle3DMode(): void {
    this.globeViewStyle.isThreeDEnabled = !this.globeViewStyle.isThreeDEnabled
  }

  @action.bound
  public toggleTerrain(): void {
    this.globeViewStyle.isTerrainEnabled = !this.globeViewStyle.isTerrainEnabled
  }

  @action.bound
  public toggleLabels(): void {
    this.globeViewStyle.shouldHideLabels = !this.globeViewStyle.shouldHideLabels
  }

  public getMap(): mapboxgl.Map | null {
    return this.mapRef?.getMap?.() ?? null
  }

  public flyTo(longitude: number, latitude: number, zoom?: number): void {
    const map = this.mapRef?.getMap?.()
    if (!map) return

    map.flyTo({
      center: [longitude, latitude],
      zoom: zoom ?? map.getZoom(),
      duration: 800,
      essential: true,
    })
  }

  public resetViewport(): void {
    const map = this.mapRef?.getMap?.()
    if (!map) return

    map.flyTo({
      center: [this.initialViewport.longitude, this.initialViewport.latitude],
      zoom: this.initialViewport.zoom,
      pitch: 0,
      bearing: 0,
      duration: 800,
      essential: true,
    })
  }

  public dispose(): void {
    this.mapRef = null
    this.mapReadyCallbacks = []
  }
}
